import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import platformConfigService from '../services/platformConfigService';
import { runMigrationIfNeeded } from '../utils/migrateLocalStorageToFirebase';
import { preparePlatformIntegration, disablePlatformIntegration, getActivePlatforms } from '../utils/ecommerceIntegrations';

export interface EcommercePlatform {
  id: string;
  name: string;
  enabled: boolean;
  description: string;
  icon: string;
  apiKey?: string;
  apiSecret?: string;
  connected: boolean;
  locked?: boolean; // Plataformas que aún no están disponibles
}

interface UseEcommercePlatformsReturn {
  platforms: EcommercePlatform[];
  activePlatforms: EcommercePlatform[];
  loading: boolean;
  error: string | null;
  updatePlatform: (platformId: string, updates: Partial<EcommercePlatform>) => Promise<void>;
  savePlatformSettings: () => Promise<void>;
  loadPlatformSettings: () => Promise<void>;
}

const defaultPlatforms: EcommercePlatform[] = [
  {
    id: 'mercadolibre',
    name: 'Mercado Libre',
    enabled: false,
    description: 'Marketplace líder en América Latina',
    icon: '🛒',
    connected: false
  },
  {
    id: 'amazon',
    name: 'Amazon Marketplace',
    enabled: false,
    description: 'Plataforma global de e-commerce',
    icon: '📦',
    connected: false,
    locked: true
  },
  {
    id: 'shopify',
    name: 'Shopify',
    enabled: false,
    description: 'Plataforma de comercio electrónico completa',
    icon: '🛍️',
    connected: false
  },
  {
    id: 'tiendanube',
    name: 'Tiendanube',
    enabled: false,
    description: 'Solución de e-commerce para Latinoamérica',
    icon: '☁️',
    connected: false
  },
  {
    id: 'prestashop',
    name: 'PrestaShop',
    enabled: false,
    description: 'Plataforma open source de e-commerce',
    icon: '⚡',
    connected: false,
    locked: true
  },
  {
    id: 'magento',
    name: 'Magento',
    enabled: false,
    description: 'Plataforma empresarial de e-commerce',
    icon: '🏪',
    connected: false,
    locked: true
  },
  {
    id: 'wix',
    name: 'Wix eCommerce',
    enabled: false,
    description: 'Constructor de sitios web con e-commerce',
    icon: '🎨',
    connected: false,
    locked: true
  },
  {
    id: 'bigcommerce',
    name: 'BigCommerce',
    enabled: false,
    description: 'Plataforma SaaS de e-commerce',
    icon: '🏢',
    connected: false,
    locked: true
  },
  {
    id: 'vtex',
    name: 'VTEX',
    enabled: false,
    description: 'Plataforma de comercio digital',
    icon: '🌐',
    connected: false,
    locked: true
  }
];

export const useEcommercePlatforms = (): UseEcommercePlatformsReturn => {
  const { user } = useAuth();
  const [platforms, setPlatforms] = useState<EcommercePlatform[]>(defaultPlatforms);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPlatformSettings = useCallback(async () => {
    if (!user) {
      setPlatforms(defaultPlatforms);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Ejecutar migración si es necesaria
      await runMigrationIfNeeded(user.uid);

      // Cargar configuraciones desde Firebase
      const savedConfigs = await platformConfigService.getEcommerceConfigs();
      
      if (savedConfigs && savedConfigs.length > 0) {
        // Convertir de formato Firebase a formato del hook
        const convertedPlatforms = savedConfigs.map(config => ({
          id: config.id,
          name: config.name,
          enabled: config.enabled,
          description: config.description,
          icon: config.icon,
          connected: config.connected,
          apiKey: config.apiKey,
          apiSecret: config.apiSecret
        }));
        setPlatforms(convertedPlatforms);
        console.log('✅ Configuraciones de plataformas cargadas desde Firebase:', convertedPlatforms);
        
        // Debug: verificar el estado después de cargar
        console.log('🔍 Estado de MercadoLibre después de cargar:', 
          convertedPlatforms.find(p => p.id === 'mercadolibre'));
        
        // Forzar re-render del componente
        setTimeout(() => {
          console.log('🔄 Estado actualizado después de timeout:', convertedPlatforms);
        }, 100);
      } else {
        console.log('📝 No hay configuraciones guardadas, usando valores por defecto');
        setPlatforms(defaultPlatforms);
      }
    } catch (err) {
      console.error('❌ Error cargando configuraciones de plataformas:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
      setPlatforms(defaultPlatforms);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const savePlatformSettings = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Convertir plataformas a formato Firebase
      const firebaseConfigs = platforms.map(platform => ({
        id: platform.id,
        name: platform.name,
        enabled: platform.enabled,
        description: platform.description,
        icon: platform.icon,
        connected: platform.connected,
        apiKey: platform.apiKey,
        apiSecret: platform.apiSecret,
        lastUpdated: new Date() // Agregar timestamp actual
      }));

      // Guardar en Firebase
      await platformConfigService.updateEcommerceConfigs(firebaseConfigs);
      console.log('✅ Configuraciones de plataformas guardadas en Firebase:', firebaseConfigs);

      // Aquí se podría implementar la lógica del backend:
      // 1. Validar que las plataformas activadas tengan las credenciales necesarias
      // 2. Preparar las integraciones de API correspondientes
      // 3. Actualizar el estado de conexión de cada plataforma
      // 4. Sincronizar con el sistema de publicación con IA

      // Simulamos la preparación de integraciones
      console.log('🔧 Preparando integraciones de plataformas...');
      await preparePlatformIntegrations();
      console.log('✅ Integraciones preparadas');
      
    } catch (err) {
      console.error('❌ Error guardando configuraciones de plataformas:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [user, platforms]);

  const preparePlatformIntegrations = async () => {
    const enabledPlatforms = platforms.filter(p => p.enabled);
    
    console.log('🔧 Preparando integraciones para plataformas activas:', enabledPlatforms.map(p => p.name));
    
    // Preparar integraciones para plataformas habilitadas
    for (const platform of enabledPlatforms) {
      if (!platform.connected) {
        try {
          // Simulamos credenciales básicas para la demostración
          const credentials = {
            client_id: 'demo_client_id',
            client_secret: 'demo_client_secret'
          };

          const result = await preparePlatformIntegration(platform.id, credentials);
          
          if (result.success && result.integration) {
            setPlatforms(prev => 
              prev.map(p => 
                p.id === platform.id 
                  ? { ...p, connected: true }
                  : p
              )
            );
            console.log(`✅ Integración preparada para ${platform.name}`);
          } else {
            console.error(`❌ Error preparando integración para ${platform.name}:`, result.error);
          }
        } catch (error) {
          console.error(`❌ Error preparando integración para ${platform.name}:`, error);
        }
      }
    }

    // Deshabilitar integraciones para plataformas deshabilitadas
    const disabledPlatforms = platforms.filter(p => !p.enabled && p.connected);
    for (const platform of disabledPlatforms) {
      try {
        const result = await disablePlatformIntegration(platform.id);
        
        if (result.success) {
          setPlatforms(prev => 
            prev.map(p => 
              p.id === platform.id 
                ? { ...p, connected: false }
                : p
            )
          );
          console.log(`✅ Integración deshabilitada para ${platform.name}`);
        } else {
          console.error(`❌ Error deshabilitando integración para ${platform.name}:`, result.error);
        }
      } catch (error) {
        console.error(`❌ Error deshabilitando integración para ${platform.name}:`, error);
      }
    }
  };

  const updatePlatform = useCallback(async (platformId: string, updates: Partial<EcommercePlatform>) => {
    setPlatforms(prev => 
      prev.map(platform => 
        platform.id === platformId 
          ? { ...platform, ...updates }
          : platform
      )
    );
  }, []);

  useEffect(() => {
    // Solo cargar una vez al inicializar
    loadPlatformSettings();
  }, []); // Sin dependencias para que solo se ejecute una vez

  // Solo las plataformas que están habilitadas Y conectadas están listas para publicar
  const activePlatforms = platforms.filter(platform => platform.enabled && platform.connected);
  
  // Debug: verificar plataformas activas
  console.log('🔍 Plataformas activas (enabled && connected):', activePlatforms.map(p => ({
    id: p.id,
    name: p.name,
    enabled: p.enabled,
    connected: p.connected
  })));

  return {
    platforms,
    activePlatforms,
    loading,
    error,
    updatePlatform,
    savePlatformSettings,
    loadPlatformSettings
  };
};
